import { Component, ChangeDetectionStrategy, computed, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronRight, faClipboardCheck } from '@fortawesome/pro-duotone-svg-icons';

import { StatusBadgeComponent } from '../../../status-badge/status-badge.component';
import { LicenseSectionComponent } from '../../../license-section/license-section.component';
import { ProvenanceSectionComponent } from '../../../provenance-section/provenance-section.component';
import { ComplianceLockSectionComponent } from '../../../compliance-lock-section/compliance-lock-section.component';
import { ComplianceCheckDialogService } from '../../../compliance-check-dialog/compliance-check-dialog.service';
import { SystemAccessService } from '../../../../services/system-access.service';
import { LocaleService } from '../../../../services/locale.service';
import { Item } from '../../../../models/item';
import { GovernanceBadge, deriveLicenseBadge, deriveLockBadge, deriveSourceBadge } from './governance-status.utils';

type GovernanceRowKey = 'source' | 'license' | 'lock';

interface GovernanceRow {
    key: GovernanceRowKey;
    labelKey: string;
    badge: GovernanceBadge;
}

/**
 * "Rights & compliance" section for the file-preview INFO tab.
 * Three status ROWS (Source / License / Lock) — label + badge + chevron, expanding in
 * place to the EXISTING governance components hosted `embedded`. Rows default-collapsed
 * on every open; no persistence; no auto-expand.
 *
 * Disclosure rows are hand-rolled, NOT <p-accordion> (a deliberate deviation:
 * the accordion's admin-recipe header chrome fights the dense label+badge+chevron geometry;
 * three fixed rows on one signal are less code than an accordion restyle). A11y contract:
 * <button> rows, aria-expanded/aria-controls, chevron transition off under reduced-motion.
 *
 * The Check action is SECTION-OWNED: gate + dialog-open live here, in ONE home — the
 * exact predicate formerly duplicated in the media/drive preview-context providers.
 */
@Component({
    selector: 'app-governance-section',
    standalone: true,
    imports: [
        TranslateModule, ButtonModule, FontAwesomeModule, StatusBadgeComponent,
        LicenseSectionComponent, ProvenanceSectionComponent, ComplianceLockSectionComponent,
    ],
    templateUrl: './governance-section.component.html',
    styleUrls: ['./governance-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GovernanceSectionComponent {
    /** The previewed asset (full tier — carries `license` + `provenance` + `complianceLock`). */
    readonly item = input.required<Item>();
    /** Per-row gates passed down by the host (today's section gates). The lock row is unconditional for Files. */
    readonly showLicense = input(false);
    readonly showProvenance = input(false);
    /** Re-emits the embedded children's item patches (host forwards to its own itemUpdated plumbing). */
    readonly itemUpdated = output<Item>();

    protected readonly faChevronRight = faChevronRight;
    protected readonly faClipboardCheck = faClipboardCheck;

    private readonly systemAccess = inject(SystemAccessService);
    private readonly complianceCheckDialog = inject(ComplianceCheckDialogService);
    private readonly localeService = inject(LocaleService);

    /** Badge clock — captured once per instance (DS2: the panel is per-open short-lived, staleness immaterial). */
    private readonly now = new Date();

    /** Rows default-collapsed; toggle via update() (L-012). */
    private readonly expandedRows = signal<ReadonlySet<GovernanceRowKey>>(new Set());

    readonly rows = computed<GovernanceRow[]>(() => {
        const item = this.item();
        const rows: GovernanceRow[] = [];
        if (this.showProvenance()) {
            rows.push({ key: 'source', labelKey: 'compliance.provenanceSection.label', badge: deriveSourceBadge(item.provenance) });
        }
        if (this.showLicense()) {
            // The badge's name slot reads the license's NAMES through the operational rule (the reader's language,
            // then the brand's label languages) — a sentence slot, so no chip can render beside it; the wire's
            // `name` is the server's label-language pick and never the name to show.
            const licenseName = this.localeService.resolveOperationalLabel(item.license?.names).text ?? null;
            rows.push({ key: 'license', labelKey: 'filePreview.license', badge: deriveLicenseBadge(item.license, this.now, licenseName) });
        }
        rows.push({ key: 'lock', labelKey: 'filePreview.governance.lock', badge: deriveLockBadge(item.complianceLock) });
        return rows;
    });

    /** Section-owned Check gate: ViewCompliance ∪ admin. */
    readonly canCheck = computed(() =>
        this.systemAccess.permissions().has('VIEW_COMPLIANCE') || this.systemAccess.admin());

    isExpanded(key: GovernanceRowKey): boolean {
        return this.expandedRows().has(key);
    }

    toggle(key: GovernanceRowKey): void {
        this.expandedRows.update(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }

    openCheck(): void {
        this.complianceCheckDialog.open(this.item().id);
    }
}
